import { Link, createFileRoute } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useMemo } from 'react';

import { Card } from '@/components/Card';
import { ErrorBanner } from '@/components/ErrorBanner';
import { PageHeader } from '@/components/PageHeader';
import { Spinner } from '@/components/Spinner';
import { AdherenceChart, type AdherencePoint } from '@/components/food/AdherenceChart';
import { formatKcal, formatShortDay, shiftIsoDate, todayIso } from '@/components/food/format';
import {
  TREND_RANGES,
  trendsSchema,
  type TrendDays,
  type TrendsSearch,
} from '@/components/food/searchParams';
import { cn } from '@/lib/cn';
import { useMealRange } from '@/lib/queries/meals';

export const Route = createFileRoute('/_app/food/trends')({
  validateSearch: (search: Record<string, unknown>): TrendsSearch => trendsSchema.parse(search),
  component: TrendsPage,
});

/**
 * Cómo ha ido la dieta en los últimos días.
 *
 * La adherencia se promedia **sólo sobre los días registrados**: contar como
 * 0 % los días en que no se apuntó nada mezclaría «comí poco» con «no lo
 * apunté», que no es lo mismo y hundiría la media sin motivo.
 */
function TrendsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const days: TrendDays = search.days ?? 14;

  const to = todayIso();
  const from = shiftIsoDate(to, -(days - 1));
  const query = useMealRange(from, to);

  const points = useMemo<AdherencePoint[]>(
    () =>
      (query.data?.items ?? []).map((item) => ({
        label: formatShortDay(item.date),
        kcal: Math.round(item.totals.kcal),
        target: item.targets?.kcal ?? null,
      })),
    [query.data],
  );

  const logged = (query.data?.items ?? []).filter((item) => item.totals.kcal > 0);
  const withAdherence = logged.filter((item) => item.adherencePct !== null);
  const averageAdherence =
    withAdherence.length === 0
      ? null
      : withAdherence.reduce((sum, item) => sum + (item.adherencePct ?? 0), 0) /
        withAdherence.length;
  const averageKcal =
    logged.length === 0
      ? null
      : logged.reduce((sum, item) => sum + item.totals.kcal, 0) / logged.length;

  return (
    <>
      <PageHeader
        title="Tendencias"
        subtitle="Calorías por día frente a tu objetivo."
        action={
          <Link
            to="/food"
            search={{}}
            aria-label="Volver al diario"
            className="flex size-11 items-center justify-center rounded-xl border border-line text-ink-muted transition-colors hover:bg-surface-raised active:bg-surface-raised"
          >
            <ArrowLeft className="size-5" aria-hidden="true" />
          </Link>
        }
      />

      <div className="flex flex-col gap-4">
        <div
          role="tablist"
          aria-label="Periodo"
          className="flex gap-1 rounded-xl bg-surface-raised p-1"
        >
          {TREND_RANGES.map((option) => (
            <button
              key={option}
              type="button"
              role="tab"
              aria-selected={option === days}
              onClick={() => void navigate({ search: { days: option }, replace: true })}
              className={cn(
                'min-h-11 flex-1 rounded-lg text-sm font-semibold transition-colors',
                option === days
                  ? 'bg-brand text-brand-ink'
                  : 'text-ink-muted hover:bg-surface-overlay active:bg-surface-overlay',
              )}
            >
              {option} días
            </button>
          ))}
        </div>

        {query.isPending ? (
          <div className="flex justify-center py-10">
            <Spinner size="lg" />
          </div>
        ) : query.isError ? (
          <ErrorBanner
            title="No hemos podido cargar tus tendencias"
            error={query.error}
            onRetry={() => void query.refetch()}
          />
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2">
              <Stat
                label="Adherencia"
                value={averageAdherence === null ? '—' : `${Math.round(averageAdherence)} %`}
              />
              <Stat
                label="Media diaria"
                value={averageKcal === null ? '—' : formatKcal(averageKcal)}
              />
              <Stat label="Días con registro" value={`${logged.length}/${days}`} />
            </div>

            <Card
              title="Calorías por día"
              description="Las barras son lo comido; la línea, tu objetivo."
            >
              <AdherenceChart data={points} />
            </Card>

            <Card title="Detalle">
              {logged.length === 0 ? (
                <p className="text-sm text-ink-muted">
                  Todavía no has apuntado nada en este periodo.
                </p>
              ) : (
                <ul className="flex flex-col divide-y divide-line">
                  {[...(query.data.items ?? [])]
                    .reverse()
                    .filter((item) => item.totals.kcal > 0)
                    .map((item) => (
                      <li
                        key={item.date}
                        className="flex items-baseline justify-between gap-2 py-2 text-sm"
                      >
                        <span className="text-ink-muted">{formatShortDay(item.date)}</span>
                        <span className="text-ink tabular-nums">
                          {formatKcal(item.totals.kcal)}
                          {item.adherencePct === null ? null : (
                            <span className="ml-2 text-xs text-ink-faint">
                              {Math.round(item.adherencePct)} %
                            </span>
                          )}
                        </span>
                      </li>
                    ))}
                </ul>
              )}
            </Card>
          </>
        )}
      </div>
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-line bg-surface-raised px-2 py-3 text-center xs:px-3">
      <p className="text-xs tracking-wide text-ink-faint uppercase">{label}</p>
      <p className="mt-1 text-base font-bold text-ink tabular-nums">{value}</p>
    </div>
  );
}
